// Public Jobs Screen (Web Only)
// Browse jobs without authentication - Apply button shows auth modal

import React, { useState, useMemo, useEffect } from 'react';
import { View, Text, TouchableOpacity, TextInput, ScrollView, ActivityIndicator, Pressable } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useDebounce } from '../../hooks/useDebounce';
import {
  Search, X, AlertTriangle, ClipboardList, Briefcase, MapPin, DollarSign,
  Calendar, CalendarDays, Stethoscope, ChevronDown, Globe, Check, ChevronRight, Lightbulb,
  Filter,
} from 'lucide-react';
import WebLayout from '../../components/web/WebLayout';
import Pagination from '../../components/web/Pagination';
import AuthChoiceModal from '../../components/web/AuthChoiceModal';
import MobileDetailBackBar from '../../components/web/MobileDetailBackBar';
import { webColors } from '../../constants/webStyles';
import { COUNTRIES, ENABLED_COUNTRIES } from '../../constants/countries';
import { getProfessionTypes, getProfessions } from '../../services/shared';
import { getLocalizedProfessionName, getLocalizedProfessionTypeName, sortProfessionsGeneralFirst } from '../../utils/professionUtils';
import { getAllJobs, getJob, getJobFacilities } from '../../services/job';
import { getJobDisplayCurrency } from '../../utils/currency';
import { isFullTimeJob } from '../../constants/lov/professions';
import { useUserGeolocation } from '../../hooks/useUserGeolocation';
import { useIsMobile } from '../../hooks/useIsMobile';

// Import shared components
import {
  JobListCard,
  JobDetailContent,
  getParentTypeCode,
  getQuickRangeDates,
  JOB_TYPE_OPTIONS,
  DATE_POSTED_OPTIONS,
  QUICK_RANGE_OPTIONS,
  professionTypeIcons,
  sharedStyles,
} from './components';

const JOBS_PER_PAGE = 6;

const PublicJobsScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { loggedIn, country: userCountry, currencyPreset, isDoctor } = useSelector((state) => state.user);
  const isMobile = useIsMobile();

  // Get jobId from URL route params (for /job/:jobId routes)
  const jobIdFromUrl = route.params?.jobId;

  const [searchQuery, setSearchQuery] = useState('');
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [selectedJobType, setSelectedJobType] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('MY'); // Default to Malaysia for non-logged-in users
  const [selectedJob, setSelectedJob] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showJobTypeDropdown, setShowJobTypeDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showProfessionDropdown, setShowProfessionDropdown] = useState(false);
  const [selectedProfessions, setSelectedProfessions] = useState([]);
  const [expandedProfessionTypes, setExpandedProfessionTypes] = useState([]);
  const [expandedSubtypes, setExpandedSubtypes] = useState([]);
  const [professionSearchQuery, setProfessionSearchQuery] = useState('');
  const [showDatePostedDropdown, setShowDatePostedDropdown] = useState(false);
  const [showSalaryDropdown, setShowSalaryDropdown] = useState(false);
  const [selectedDatePosted, setSelectedDatePosted] = useState(null); // '24hours', '3days', etc. or null
  const [salaryMin, setSalaryMin] = useState('');
  const [salaryMax, setSalaryMax] = useState('');
  // Temporary salary values for the dropdown (only applied on "Apply" click)
  const [tempSalaryMin, setTempSalaryMin] = useState('');
  const [tempSalaryMax, setTempSalaryMax] = useState('');
  // Date filter tab state: 'posted' or 'shift'
  const [currentDateTab, setCurrentDateTab] = useState('posted');
  // Shift date values (actual applied values for Locum shift date filtering)
  const [shiftDateFrom, setShiftDateFrom] = useState('');
  const [shiftDateTo, setShiftDateTo] = useState('');
  // Temporary shift date values for the dropdown
  const [tempShiftDateFrom, setTempShiftDateFrom] = useState('');
  const [tempShiftDateTo, setTempShiftDateTo] = useState('');
  // Selected quick range for shift date
  const [selectedQuickRange, setSelectedQuickRange] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileView, setMobileView] = useState('list'); // mobile only: 'list' | 'detail'
  const [showFiltersSheet, setShowFiltersSheet] = useState(false);

  // Browser geolocation for country detection
  const {
    countryCode: detectedCountry,
    loading: geoLoading,
    error: geoError,
  } = useUserGeolocation({ autoDetect: true, timeout: 10000 });
  const [geolocationApplied, setGeolocationApplied] = useState(false);
  const [shouldHighlightCountry, setShouldHighlightCountry] = useState(false);

  // Debounced search values for API calls (300ms delay)
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const debouncedLocationSearchQuery = useDebounce(locationSearchQuery, 300);

  // Country filter options from enabled countries
  const COUNTRY_OPTIONS = ENABLED_COUNTRIES.map(code => ({
    code,
    label: COUNTRIES[code].name,
    flag: COUNTRIES[code].flag,
  }));

  // Fetch profession types
  const { data: professionTypesData, isLoading: professionTypesLoading } = useQuery({
    queryKey: ['professionTypes'],
    queryFn: getProfessionTypes,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch professions based on selected country
  const { data: professionsData, isLoading: professionsLoading } = useQuery({
    queryKey: ['professions', selectedCountry],
    queryFn: () => getProfessions(selectedCountry || 'MY'),
    staleTime: 5 * 60 * 1000,
  });

  // Organize professions by type with 3-level hierarchy:
  // Level 1: Parent types (e.g. "a" = Doctor, "e-x" = Medical Assistant)
  // Level 2: Sub-types (e.g. "a-a" = General Practitioner, "a-b" = Specialist)
  // Level 3: Professions (e.g. "a-a-1" = Addiction Medicine GP)
  const organizedProfessions = useMemo(() => {
    const professionTypes = professionTypesData?.data?.data || [];
    const professions = professionsData?.data?.data || [];

    const hierarchy = {};

    // Separate parent types and sub-types from profession types
    professionTypes.forEach(type => {
      const code = type.code;
      const isTopLevel = !code.includes('-') || /^[a-z]-x$/i.test(code);
      if (isTopLevel) {
        hierarchy[code] = {
          ...type,
          subtypes: [],
          professions: [], // direct professions for types without subtypes
        };
      }
    });

    // Add sub-types to their parent types
    professionTypes.forEach(type => {
      const code = type.code;
      const isTopLevel = !code.includes('-') || /^[a-z]-x$/i.test(code);
      if (!isTopLevel) {
        const parentCode = getParentTypeCode(code);
        if (hierarchy[parentCode]) {
          hierarchy[parentCode].subtypes.push({
            ...type,
            professions: [],
          });
        }
      }
    });

    // Add professions to their respective sub-types or parent types
    professions.forEach(profession => {
      const typeCode = profession.profession_type_code;
      if (!typeCode || typeof typeCode !== 'string') return;

      const parentCode = getParentTypeCode(typeCode);
      if (!hierarchy[parentCode]) return;

      // Try to place under matching sub-type
      const subType = hierarchy[parentCode].subtypes.find(s => s.code === typeCode);
      if (subType) {
        subType.professions.push(profession);
      } else {
        // No matching sub-type, add directly to parent
        hierarchy[parentCode].professions.push(profession);
      }
    });

    // Sort sub-types and professions alphabetically, then pin each list's
    // "general" entry (e.g. General Practitioner within Doctor > GP) to the top
    Object.values(hierarchy).forEach(type => {
      type.subtypes.sort((a, b) => {
        const nameA = getLocalizedProfessionTypeName(a, selectedCountry);
        const nameB = getLocalizedProfessionTypeName(b, selectedCountry);
        return nameA.localeCompare(nameB);
      });
      type.subtypes.forEach(sub => {
        sub.professions.sort((a, b) => {
          const nameA = getLocalizedProfessionName(a, selectedCountry);
          const nameB = getLocalizedProfessionName(b, selectedCountry);
          return nameA.localeCompare(nameB);
        });
        sub.professions = sortProfessionsGeneralFirst(sub.professions, sub.code, selectedCountry);
      });
      type.professions.sort((a, b) => {
        const nameA = getLocalizedProfessionName(a, selectedCountry);
        const nameB = getLocalizedProfessionName(b, selectedCountry);
        return nameA.localeCompare(nameB);
      });
      type.professions = sortProfessionsGeneralFirst(type.professions, type.code, selectedCountry);
    });

    return hierarchy;
  }, [professionTypesData, professionsData, selectedCountry]);

  // Filter professions based on search query (searches across all 3 levels)
  const filteredProfessions = useMemo(() => {
    if (!professionSearchQuery) return organizedProfessions;

    const filtered = {};
    const lowerSearch = professionSearchQuery.toLowerCase();

    Object.entries(organizedProfessions).forEach(([code, type]) => {
      const typeName = getLocalizedProfessionTypeName(type, selectedCountry);
      const typeMatches = typeName.toLowerCase().includes(lowerSearch);

      if (typeMatches) {
        // Parent type matches — include everything under it
        filtered[code] = { ...type };
        return;
      }

      // Check sub-types and their professions
      const matchingSubtypes = [];
      type.subtypes.forEach(subType => {
        const subTypeName = getLocalizedProfessionTypeName(subType, selectedCountry);
        const subTypeMatches = subTypeName.toLowerCase().includes(lowerSearch);

        if (subTypeMatches) {
          matchingSubtypes.push({ ...subType });
        } else {
          const matchingProfs = subType.professions.filter(p => {
            const profName = getLocalizedProfessionName(p, selectedCountry);
            return profName.toLowerCase().includes(lowerSearch);
          });
          if (matchingProfs.length > 0) {
            matchingSubtypes.push({ ...subType, professions: matchingProfs });
          }
        }
      });

      // Check direct professions (for types without subtypes)
      const matchingDirectProfessions = type.professions.filter(p => {
        const profName = getLocalizedProfessionName(p, selectedCountry);
        return profName.toLowerCase().includes(lowerSearch);
      });

      if (matchingSubtypes.length > 0 || matchingDirectProfessions.length > 0) {
        filtered[code] = {
          ...type,
          subtypes: matchingSubtypes,
          professions: matchingDirectProfessions,
        };
      }
    });

    return filtered;
  }, [organizedProfessions, professionSearchQuery, selectedCountry]);

  // Redirect logged-in users to authenticated Jobs page
  useEffect(() => {
    if (loggedIn) {
      navigation.replace('Jobs');
    }
  }, [loggedIn, navigation]);

  // Reset profession selections when country changes (professions are country-specific)
  useEffect(() => {
    setSelectedProfessions([]);
    setExpandedProfessionTypes([]);
    setExpandedSubtypes([]);
    setProfessionSearchQuery('');
  }, [selectedCountry]);

  // Auto-select country based on browser geolocation
  useEffect(() => {
    if (!geoLoading && detectedCountry && !geolocationApplied) {
      setSelectedCountry(detectedCountry);
      setGeolocationApplied(true);
    }
  }, [geoLoading, detectedCountry, geolocationApplied]);

  // Highlight country dropdown if geolocation was denied or errored
  useEffect(() => {
    if (!geoLoading && geoError === 'denied') {
      setShouldHighlightCountry(true);
      // Auto-clear highlight after 5 seconds
      const timer = setTimeout(() => setShouldHighlightCountry(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [geoLoading, geoError]);

  // Reset to first page and clear selected job when any filter changes
  useEffect(() => {
    setCurrentPage(1);
    setSelectedJob(null);
    setMobileView('list');
  }, [debouncedSearchQuery, debouncedLocationSearchQuery, selectedJobType, selectedCountry, selectedProfessions, selectedDatePosted, shiftDateFrom, shiftDateTo, salaryMin, salaryMax]);

  // Auto-select first job when page changes
  useEffect(() => {
    if (paginatedJobs.length > 0) {
      setSelectedJob(paginatedJobs[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only on page change to avoid conflicts with URL-based auto-select
  }, [currentPage]);

  // Convert selectedProfessions to Set for O(1) lookup performance
  const selectedProfessionsSet = useMemo(() => new Set(selectedProfessions), [selectedProfessions]);

  // Toggle profession type expansion (level 1)
  const toggleProfessionType = (typeCode) => {
    setExpandedProfessionTypes(prev =>
      prev.includes(typeCode)
        ? prev.filter(c => c !== typeCode)
        : [...prev, typeCode]
    );
  };

  // Toggle sub-type expansion (level 2)
  const toggleSubtype = (subtypeCode) => {
    setExpandedSubtypes(prev =>
      prev.includes(subtypeCode)
        ? prev.filter(c => c !== subtypeCode)
        : [...prev, subtypeCode]
    );
  };

  // Toggle profession selection
  const toggleProfession = (professionId) => {
    setSelectedProfessions(prev =>
      prev.includes(professionId)
        ? prev.filter(id => id !== professionId)
        : [...prev, professionId]
    );
  };

  // Handle quick range selection for shift dates
  const handleQuickRangeSelect = (range) => {
    setSelectedQuickRange(range);
    const { from, to } = getQuickRangeDates(range);
    setTempShiftDateFrom(from);
    setTempShiftDateTo(to);
  };

  // Build query string for API (using debounced search values)
  const queryString = useMemo(() => {
    const params = [];
    if (debouncedSearchQuery) params.push(`text=${encodeURIComponent(debouncedSearchQuery)}`);
    if (debouncedLocationSearchQuery) params.push(`address_query=${encodeURIComponent(debouncedLocationSearchQuery)}`);
    if (selectedJobType !== null && selectedJobType !== 4) {
      params.push(`job_type=${selectedJobType}`);
    }
    if (selectedJobType === 4) {
      // International: filter by target country via 'international' param,
      // skip 'country' param (that filters by clinic base location, not target)
      if (selectedCountry) params.push(`international=${selectedCountry}`);
    } else {
      if (selectedCountry) params.push(`country=${selectedCountry}`);
    }
    if (selectedProfessions.length > 0) params.push(`profession_ids=${selectedProfessions.join(',')}`);

    // Date Posted filter (Posted Date tab)
    if (selectedDatePosted) {
      const option = DATE_POSTED_OPTIONS.find(opt => opt.value === selectedDatePosted);
      if (option) {
        const createdMin = moment().subtract(option.days, 'days').startOf('day').valueOf();
        const createdMax = moment().endOf('day').valueOf();
        params.push(`created_min=${createdMin}`);
        params.push(`created_max=${createdMax}`);
      }
    }

    // Shift Date filter (Shift Date tab - for Locum jobs)
    // Backend expects min_timestamp/max_timestamp (filters on start_timestamp/end_timestamp columns)
    if (shiftDateFrom || shiftDateTo) {
      if (shiftDateFrom && moment(shiftDateFrom, 'YYYY-MM-DD', true).isValid()) {
        const shiftFromMoment = moment(shiftDateFrom);
        const isToday = shiftFromMoment.isSame(moment(), 'day');
        // If shiftDateFrom is today, use current time; otherwise use start of day
        const shiftMin = isToday ? moment().valueOf() : shiftFromMoment.startOf('day').valueOf();
        params.push(`min_timestamp=${shiftMin}`);
      }
      if (shiftDateTo && moment(shiftDateTo, 'YYYY-MM-DD', true).isValid()) {
        const shiftMax = moment(shiftDateTo).endOf('day').valueOf();
        params.push(`max_timestamp=${shiftMax}`);
      }
    }

    // Salary filter
    if (salaryMin) params.push(`min_price=${salaryMin}`);
    if (salaryMax) params.push(`max_price=${salaryMax}`);

    return params.join('&');
  }, [debouncedSearchQuery, debouncedLocationSearchQuery, selectedJobType, selectedCountry, selectedProfessions, selectedDatePosted, shiftDateFrom, shiftDateTo, salaryMin, salaryMax]);

  const { data: jobsData, isLoading, error } = useQuery({
    queryKey: ['publicJobs', { query: queryString }],
    queryFn: getAllJobs,
  });
  const jobsRawResult = jobsData?.data?.data || jobsData?.data || [];
  const jobsRaw = Array.isArray(jobsRawResult) ? jobsRawResult : [];

  // Reorder jobs to move URL job to top (only when accessed via /job/:jobId)
  const jobs = useMemo(() => {
    if (!jobIdFromUrl || jobsRaw.length === 0) return jobsRaw;

    const urlJobIndex = jobsRaw.findIndex(job => job.uuid === jobIdFromUrl);
    if (urlJobIndex <= 0) return jobsRaw; // Already at top or not found

    // Move the URL job to the top
    const reordered = [...jobsRaw];
    const [urlJob] = reordered.splice(urlJobIndex, 1);
    reordered.unshift(urlJob);
    return reordered;
  }, [jobsRaw, jobIdFromUrl]);

  const totalPages = Math.ceil(jobs.length / JOBS_PER_PAGE);
  const paginatedJobs = useMemo(() => {
    const start = (currentPage - 1) * JOBS_PER_PAGE;
    return jobs.slice(start, start + JOBS_PER_PAGE);
  }, [jobs, currentPage]);

  // Fetch detailed job data when a job is selected
  const { data: jobDetailQuery, isLoading: detailLoading } = useQuery({
    queryKey: ['publicJob', selectedJob?.uuid],
    queryFn: () => getJob(selectedJob?.uuid),
    enabled: !!selectedJob?.uuid,
  });
  const jobDetail = jobDetailQuery?.data?.data || jobDetailQuery?.data || selectedJob;

  // Fetch job facilities for clinical expertise
  const { data: jobFacilitiesQuery } = useQuery({
    queryKey: ['publicJobFacilities', selectedJob?.uuid],
    queryFn: () => getJobFacilities(selectedJob?.uuid),
    enabled: !!selectedJob?.uuid,
  });
  const jobFacilityData = jobFacilitiesQuery?.data?.data;

  // Auto-select job: prioritize URL jobId if present and found in list, otherwise select first job
  useEffect(() => {
    if (jobs.length > 0 && !selectedJob) {
      if (jobIdFromUrl) {
        // Try to find the job from URL in the current list
        const jobFromUrl = jobs.find(job => job.uuid === jobIdFromUrl);
        if (jobFromUrl) {
          if (isMobile) setMobileView('detail');
          setSelectedJob(jobFromUrl);
          return;
        }
      }
      // Fallback to first job
      setSelectedJob(jobs[0]);
    }
  }, [jobs, selectedJob, jobIdFromUrl]);

  const handleApply = () => {
    setShowAuthModal(true);
  };

  // Calculate job details
  const isFullTime = isFullTimeJob(jobDetail?.type);
  const displayCurrency = getJobDisplayCurrency(jobDetail, userCountry || currencyPreset);
  const diffHours = Math.abs(moment(jobDetail?.start_timestamp).diff(moment(jobDetail?.end_timestamp), 'hours') || 1);

  // Helper function to get currency symbol based on selected country
  const getCurrencySymbol = () => {
    return COUNTRIES[selectedCountry]?.currencySymbol || 'RM';
  };

  // Helper function to get salary unit label based on job type
  const getSalaryUnitLabel = () => {
    // Locum jobs (type 0) show per hour, others show per month
    if (selectedJobType === null || selectedJobType === 0) {
      return '/hour';
    }
    return '/month';
  };

  const activeFilterCount = [
    !!locationSearchQuery,
    selectedJobType !== null,
    selectedCountry !== 'MY',
    selectedProfessions.length > 0,
    selectedDatePosted !== null,
    !!(shiftDateFrom || shiftDateTo),
    !!(salaryMin || salaryMax),
  ].filter(Boolean).length;

  const filterControls = (
    <>
      {/* Job Type Dropdown */}
      <View style={sharedStyles.filterDropdownWrapper}>
        <Pressable
          style={[sharedStyles.filterPill, selectedJobType !== null && sharedStyles.filterPillActive, isMobile && sharedStyles.filterPillMobile]}
          onPress={() => {
            setShowJobTypeDropdown(!showJobTypeDropdown);
            setShowCountryDropdown(false);
            setShowProfessionDropdown(false);
          }}
        >
          <Briefcase size={16} color={selectedJobType !== null ? webColors.primary : webColors.textSecondary} />
          <Text style={[sharedStyles.filterPillText, selectedJobType !== null && sharedStyles.filterPillTextActive]}>
            {selectedJobType !== null
              ? JOB_TYPE_OPTIONS.find(opt => opt.id === selectedJobType)?.label
              : 'Job Type'}
          </Text>
          <ChevronDown size={16} color={selectedJobType !== null ? webColors.primary : webColors.textSecondary} />
        </Pressable>
        {showJobTypeDropdown && (
          <>
            {!isMobile && <Pressable style={sharedStyles.dropdownBackdrop} onPress={() => setShowJobTypeDropdown(false)} />}
            <View style={[sharedStyles.filterDropdown, isMobile && sharedStyles.filterDropdownMobile]}>
              <Pressable
                style={[sharedStyles.dropdownItem, selectedJobType === null && sharedStyles.dropdownItemActive]}
                onPress={() => {
                  setSelectedJobType(null);
                  setShowJobTypeDropdown(false);
                }}
              >
                <Text style={[sharedStyles.dropdownItemText, selectedJobType === null && sharedStyles.dropdownItemTextActive]}>
                  All Jobs
                </Text>
              </Pressable>
              {JOB_TYPE_OPTIONS.map((option) => (
                <Pressable
                  key={option.id}
                  style={[sharedStyles.dropdownItem, selectedJobType === option.id && sharedStyles.dropdownItemActive]}
                  onPress={() => {
                    setSelectedJobType(option.id);
                    setShowJobTypeDropdown(false);
                  }}
                >
                  <Text style={[sharedStyles.dropdownItemText, selectedJobType === option.id && sharedStyles.dropdownItemTextActive]}>
                    {option.label}
                  </Text>
                </Pressable>
              ))}
            </View>
          </>
        )}
      </View>

      {/* Country Dropdown */}
      <View style={sharedStyles.filterDropdownWrapper}>
        <Pressable
          style={[
            sharedStyles.filterPill,
            sharedStyles.filterPillActive,
            shouldHighlightCountry && sharedStyles.filterPillHighlight,
            isMobile && sharedStyles.filterPillMobile,
          ]}
          onPress={() => {
            setShowCountryDropdown(!showCountryDropdown);
            setShowJobTypeDropdown(false);
            setShowProfessionDropdown(false);
            setShouldHighlightCountry(false); // Clear highlight when user interacts
          }}
        >
          {geoLoading ? (
            <ActivityIndicator size={16} color={webColors.primary} />
          ) : (
            <Globe size={16} color={shouldHighlightCountry ? '#f59e0b' : webColors.primary} />
          )}
          <Text style={[sharedStyles.filterPillText, sharedStyles.filterPillTextActive]}>
            {geoLoading
              ? 'Detecting...'
              : `${COUNTRY_OPTIONS.find(opt => opt.code === selectedCountry)?.flag} ${COUNTRY_OPTIONS.find(opt => opt.code === selectedCountry)?.label || 'Country'}`
            }
          </Text>
          <ChevronDown size={16} color={shouldHighlightCountry ? '#f59e0b' : webColors.primary} />
        </Pressable>
        {showCountryDropdown && (
          <>
            {!isMobile && <Pressable style={sharedStyles.dropdownBackdrop} onPress={() => setShowCountryDropdown(false)} />}
            <View style={[sharedStyles.filterDropdown, isMobile && sharedStyles.filterDropdownMobile]}>
              {COUNTRY_OPTIONS.map((option) => (
                <Pressable
                  key={option.code}
                  style={[sharedStyles.dropdownItem, selectedCountry === option.code && sharedStyles.dropdownItemActive]}
                  onPress={() => {
                    setSelectedCountry(option.code);
                    setShowCountryDropdown(false);
                  }}
                >
                  <Text style={[sharedStyles.dropdownItemText, selectedCountry === option.code && sharedStyles.dropdownItemTextActive]}>
                    {option.flag} {option.label}
                  </Text>
                </Pressable>
              ))}
            </View>
          </>
        )}
      </View>

      {/* Profession Filter Dropdown */}
      <View style={sharedStyles.filterDropdownWrapper}>
        <TouchableOpacity
          style={[
            sharedStyles.filterPill,
            selectedProfessions.length > 0 && sharedStyles.filterPillActive,
            showProfessionDropdown && sharedStyles.filterPillOpen,
            isMobile && sharedStyles.filterPillMobile,
          ]}
          onPress={() => {
            setShowProfessionDropdown(!showProfessionDropdown);
            setShowJobTypeDropdown(false);
            setShowCountryDropdown(false);
          }}
        >
          <Stethoscope size={16} color={selectedProfessions.length > 0 ? webColors.primary : webColors.textMuted} />
          <Text style={[
            sharedStyles.filterPillLabel,
            selectedProfessions.length > 0 && sharedStyles.filterPillLabelActive,
          ]}>
            Profession
          </Text>
          {selectedProfessions.length > 0 && (
            <View style={sharedStyles.filterPillCount}>
              <Text style={sharedStyles.filterPillCountText}>{selectedProfessions.length}</Text>
            </View>
          )}
          <ChevronDown
            size={14}
            color={selectedProfessions.length > 0 ? webColors.primary : webColors.textMuted}
            style={showProfessionDropdown ? { transform: [{ rotate: '180deg' }] } : {}}
          />
        </TouchableOpacity>

        {/* Profession Dropdown Menu */}
        {showProfessionDropdown && (
          <>
            {!isMobile && <Pressable style={sharedStyles.dropdownBackdrop} onPress={() => setShowProfessionDropdown(false)} />}
            <View style={[sharedStyles.filterDropdown, sharedStyles.professionDropdown, isMobile && sharedStyles.filterDropdownMobile, isMobile && sharedStyles.professionDropdownMobile]}>
              <View style={sharedStyles.dropdownHeader}>
                <Text style={sharedStyles.dropdownTitle}>Select Profession & Specialty</Text>
              </View>

              {/* Search Input */}
              <View style={sharedStyles.dropdownSearch}>
                <Search size={16} color={webColors.textMuted} />
                <TextInput
                  style={sharedStyles.dropdownSearchInput}
                  placeholder="Search professions..."
                  placeholderTextColor={webColors.textMuted}
                  value={professionSearchQuery}
                  onChangeText={setProfessionSearchQuery}
                />
                {professionSearchQuery.length > 0 && (
                  <TouchableOpacity onPress={() => setProfessionSearchQuery('')}>
                    <X size={14} color={webColors.textMuted} />
                  </TouchableOpacity>
                )}
              </View>

              {/* Profession Options */}
              <ScrollView style={sharedStyles.professionDropdownOptions} showsVerticalScrollIndicator={false}>
                {(professionTypesLoading || professionsLoading) ? (
                  <View style={sharedStyles.professionLoadingContainer}>
                    <ActivityIndicator size="small" color={webColors.primary} />
                    <Text style={sharedStyles.professionLoadingText}>Loading professions...</Text>
                  </View>
                ) : Object.keys(filteredProfessions).length === 0 ? (
                  <View style={sharedStyles.professionEmptyContainer}>
                    <Text style={sharedStyles.professionEmptyText}>No professions found</Text>
                  </View>
                ) : (
                  Object.entries(filteredProfessions).map(([typeCode, typeData]) => {
                    const isExpanded = expandedProfessionTypes.includes(typeCode) || professionSearchQuery.length > 0;
                    const iconConfig = professionTypeIcons[typeCode] || professionTypeIcons.default;
                    const hasSubtypes = typeData.subtypes && typeData.subtypes.length > 0;
                    const allProfessionsInType = [
                      ...typeData.professions,
                      ...(typeData.subtypes || []).flatMap(s => s.professions),
                    ];
                    const selectedInType = allProfessionsInType.filter(p => selectedProfessionsSet.has(p.id)).length;

                    return (
                      <View key={typeCode} style={sharedStyles.professionTypeGroup}>
                        {/* Level 1: Profession Type Header */}
                        <TouchableOpacity
                          style={[
                            sharedStyles.professionTypeHeader,
                            isExpanded && sharedStyles.professionTypeHeaderExpanded,
                          ]}
                          onPress={() => toggleProfessionType(typeCode)}
                        >
                          <Text style={sharedStyles.professionTypeIcon}>{iconConfig.icon}</Text>
                          <Text style={[
                            sharedStyles.professionTypeLabel,
                            isExpanded && sharedStyles.professionTypeLabelExpanded,
                          ]}>
                            {getLocalizedProfessionTypeName(typeData, selectedCountry)}
                          </Text>
                          {selectedInType > 0 && (
                            <View style={[sharedStyles.professionTypeCount, { backgroundColor: iconConfig.bgColor }]}>
                              <Text style={[sharedStyles.professionTypeCountText, { color: iconConfig.color }]}>
                                {selectedInType}
                              </Text>
                            </View>
                          )}
                          <ChevronRight
                            size={16}
                            color={isExpanded ? webColors.primary : webColors.textMuted}
                            style={{ transform: [{ rotate: isExpanded ? '90deg' : '0deg' }] }}
                          />
                        </TouchableOpacity>

                        {/* Expanded content */}
                        {isExpanded && (
                          <View style={sharedStyles.professionItemsList}>
                            {/* Level 2: Sub-types (if any) */}
                            {hasSubtypes && typeData.subtypes.map(subType => {
                              const isSubExpanded = expandedSubtypes.includes(subType.code) || professionSearchQuery.length > 0;
                              const selectedInSub = subType.professions.filter(p => selectedProfessionsSet.has(p.id)).length;

                              return (
                                <View key={subType.code} style={sharedStyles.professionSubtypeGroup}>
                                  <TouchableOpacity
                                    style={[
                                      sharedStyles.professionSubtypeHeader,
                                      isSubExpanded && sharedStyles.professionSubtypeHeaderExpanded,
                                    ]}
                                    onPress={() => toggleSubtype(subType.code)}
                                  >
                                    <Text style={[
                                      sharedStyles.professionSubtypeLabel,
                                      isSubExpanded && sharedStyles.professionSubtypeLabelExpanded,
                                    ]}>
                                      {getLocalizedProfessionTypeName(subType, selectedCountry)}
                                    </Text>
                                    {selectedInSub > 0 && (
                                      <View style={[sharedStyles.professionSubtypeCount, { backgroundColor: iconConfig.bgColor }]}>
                                        <Text style={[sharedStyles.professionSubtypeCountText, { color: iconConfig.color }]}>
                                          {selectedInSub}
                                        </Text>
                                      </View>
                                    )}
                                    <ChevronRight
                                      size={14}
                                      color={isSubExpanded ? webColors.primary : webColors.textMuted}
                                      style={{ transform: [{ rotate: isSubExpanded ? '90deg' : '0deg' }] }}
                                    />
                                  </TouchableOpacity>

                                  {/* Level 3: Professions under sub-type */}
                                  {isSubExpanded && (
                                    <View style={sharedStyles.professionSubtypeItemsList}>
                                      {subType.professions.map(profession => {
                                        const isSelected = selectedProfessionsSet.has(profession.id);
                                        const professionName = getLocalizedProfessionName(profession, selectedCountry);
                                        if (professionName.length === 0) return null;
                                        return (
                                          <TouchableOpacity
                                            key={profession.id}
                                            style={[
                                              sharedStyles.professionItem,
                                              isSelected && sharedStyles.professionItemSelected,
                                            ]}
                                            onPress={() => toggleProfession(profession.id)}
                                          >
                                            <View style={[
                                              sharedStyles.professionCheckbox,
                                              isSelected && sharedStyles.professionCheckboxSelected,
                                            ]}>
                                              {isSelected && <Check size={12} color="#fff" />}
                                            </View>
                                            <Text style={[
                                              sharedStyles.professionItemText,
                                              isSelected && sharedStyles.professionItemTextSelected,
                                            ]}>
                                              {professionName}
                                            </Text>
                                          </TouchableOpacity>
                                        );
                                      })}
                                    </View>
                                  )}
                                </View>
                              );
                            })}

                            {/* Direct professions (for types without subtypes) */}
                            {typeData.professions.map(profession => {
                              const isSelected = selectedProfessionsSet.has(profession.id);
                              const professionName = getLocalizedProfessionName(profession, selectedCountry);
                              if (professionName.length === 0) return null;
                              return (
                                <TouchableOpacity
                                  key={profession.id}
                                  style={[
                                    sharedStyles.professionItem,
                                    isSelected && sharedStyles.professionItemSelected,
                                  ]}
                                  onPress={() => toggleProfession(profession.id)}
                                >
                                  <View style={[
                                    sharedStyles.professionCheckbox,
                                    isSelected && sharedStyles.professionCheckboxSelected,
                                  ]}>
                                    {isSelected && <Check size={12} color="#fff" />}
                                  </View>
                                  <Text style={[
                                    sharedStyles.professionItemText,
                                    isSelected && sharedStyles.professionItemTextSelected,
                                  ]}>
                                    {professionName}
                                  </Text>
                                </TouchableOpacity>
                              );
                            })}
                          </View>
                        )}
                      </View>
                    );
                  })
                )}
              </ScrollView>

              {/* Clear Selection */}
              {selectedProfessions.length > 0 && (
                <View style={sharedStyles.professionDropdownFooter}>
                  <TouchableOpacity
                    style={sharedStyles.professionClearBtn}
                    onPress={() => setSelectedProfessions([])}
                  >
                    <Text style={sharedStyles.professionClearBtnText}>Clear All ({selectedProfessions.length})</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </>
        )}
      </View>

      {/* Date Filter Dropdown (Tabbed: Posted Date + Shift Date) */}
      <View style={sharedStyles.filterDropdownWrapper}>
        <Pressable
          style={[
            sharedStyles.filterPill,
            (selectedDatePosted !== null || shiftDateFrom || shiftDateTo) && sharedStyles.filterPillActive,
            showDatePostedDropdown && sharedStyles.filterPillOpen,
            isMobile && sharedStyles.filterPillMobile,
          ]}
          onPress={() => {
            if (!showDatePostedDropdown) {
              // Initialize temp values from actual values when opening
              setTempShiftDateFrom(shiftDateFrom);
              setTempShiftDateTo(shiftDateTo);
              setSelectedQuickRange(null);
            }
            setShowDatePostedDropdown(!showDatePostedDropdown);
            setShowJobTypeDropdown(false);
            setShowCountryDropdown(false);
            setShowProfessionDropdown(false);
            setShowSalaryDropdown(false);
          }}
        >
          <Calendar size={16} color={(selectedDatePosted !== null || shiftDateFrom || shiftDateTo) ? webColors.primary : webColors.textSecondary} />
          <Text style={[sharedStyles.filterPillText, (selectedDatePosted !== null || shiftDateFrom || shiftDateTo) && sharedStyles.filterPillTextActive]}>
            {selectedDatePosted
              ? DATE_POSTED_OPTIONS.find(opt => opt.value === selectedDatePosted)?.label
              : shiftDateFrom || shiftDateTo
                ? `Shift: ${shiftDateFrom && moment(shiftDateFrom, 'YYYY-MM-DD', true).isValid() ? moment(shiftDateFrom).format('MMM D') : 'Start'} - ${shiftDateTo && moment(shiftDateTo, 'YYYY-MM-DD', true).isValid() ? moment(shiftDateTo).format('MMM D') : 'End'}`
                : 'Date'}
          </Text>
          <ChevronDown
            size={16}
            color={(selectedDatePosted !== null || shiftDateFrom || shiftDateTo) ? webColors.primary : webColors.textSecondary}
            style={showDatePostedDropdown ? { transform: [{ rotate: '180deg' }] } : {}}
          />
        </Pressable>
        {showDatePostedDropdown && (
          <>
            {!isMobile && <Pressable style={sharedStyles.dropdownBackdrop} onPress={() => setShowDatePostedDropdown(false)} />}
            <View style={[sharedStyles.filterDropdown, sharedStyles.dateFilterDropdown, isMobile && sharedStyles.filterDropdownMobile]}>
              {/* Underline Tabs */}
              <View style={sharedStyles.dateFilterTabs}>
                <Pressable
                  style={[sharedStyles.dateFilterTab, currentDateTab === 'posted' && sharedStyles.dateFilterTabActive]}
                  onPress={() => setCurrentDateTab('posted')}
                >
                  <Calendar size={14} color={currentDateTab === 'posted' ? webColors.primary : webColors.textMuted} />
                  <Text style={[sharedStyles.dateFilterTabText, currentDateTab === 'posted' && sharedStyles.dateFilterTabTextActive]}>
                    Posted Date
                  </Text>
                </Pressable>
                <Pressable
                  style={[sharedStyles.dateFilterTab, currentDateTab === 'shift' && sharedStyles.dateFilterTabActive]}
                  onPress={() => setCurrentDateTab('shift')}
                >
                  <CalendarDays size={14} color={currentDateTab === 'shift' ? webColors.primary : webColors.textMuted} />
                  <Text style={[sharedStyles.dateFilterTabText, currentDateTab === 'shift' && sharedStyles.dateFilterTabTextActive]}>
                    Shift Date
                  </Text>
                  <View style={[sharedStyles.dateFilterTabBadge, currentDateTab === 'shift' && sharedStyles.dateFilterTabBadgeActive]}>
                    <Text style={[sharedStyles.dateFilterTabBadgeText, currentDateTab === 'shift' && sharedStyles.dateFilterTabBadgeTextActive]}>
                      Locum
                    </Text>
                  </View>
                </Pressable>
              </View>

              {/* Posted Date Tab Content */}
              {currentDateTab === 'posted' && (
                <View style={sharedStyles.dateFilterTabContent}>
                  <View style={sharedStyles.dateFilterOptions}>
                    {DATE_POSTED_OPTIONS.map((option) => (
                      <Pressable
                        key={option.value}
                        style={[sharedStyles.dateFilterOption, selectedDatePosted === option.value && sharedStyles.dateFilterOptionSelected]}
                        onPress={() => {
                          setSelectedDatePosted(option.value);
                          // Clear shift date when selecting posted date
                          setShiftDateFrom('');
                          setShiftDateTo('');
                          setTempShiftDateFrom('');
                          setTempShiftDateTo('');
                          setSelectedQuickRange(null);
                          // Close dropdown immediately for better UX (consistent with most filter patterns)
                          setShowDatePostedDropdown(false);
                        }}
                      >
                        <View style={[sharedStyles.dateFilterRadio, selectedDatePosted === option.value && sharedStyles.dateFilterRadioSelected]} />
                        <Text style={sharedStyles.dateFilterOptionText}>{option.label}</Text>
                      </Pressable>
                    ))}
                  </View>
                </View>
              )}

              {/* Shift Date Tab Content */}
              {currentDateTab === 'shift' && (
                <View style={sharedStyles.dateFilterTabContent}>
                  <View style={sharedStyles.shiftDateContent}>
                    {/* Info Note */}
                    <View style={sharedStyles.shiftDateNote}>
                      <Lightbulb size={14} color="#92400e" />
                      <Text style={sharedStyles.shiftDateNoteText}>
                        Find Locum shifts available on specific dates
                      </Text>
                    </View>

                    {/* Date Inputs */}
                    <View style={sharedStyles.shiftDateRow}>
                      <View style={sharedStyles.shiftDateInputGroup}>
                        <Text style={sharedStyles.shiftDateLabel}>From</Text>
                        <TextInput
                          style={[sharedStyles.shiftDateInput, tempShiftDateFrom && sharedStyles.shiftDateInputActive]}
                          placeholder="DD-MM-YYYY"
                          placeholderTextColor={webColors.textMuted}
                          value={tempShiftDateFrom}
                          onChangeText={(val) => {
                            setTempShiftDateFrom(val);
                            setSelectedQuickRange(null);
                          }}
                          onFocus={(e) => { e.target.type = 'date'; }}
                          onBlur={(e) => { if (!tempShiftDateFrom) e.target.type = 'text'; }}
                        />
                      </View>
                      <View style={sharedStyles.shiftDateInputGroup}>
                        <Text style={sharedStyles.shiftDateLabel}>To</Text>
                        <TextInput
                          style={[sharedStyles.shiftDateInput, tempShiftDateTo && sharedStyles.shiftDateInputActive]}
                          placeholder="DD-MM-YYYY"
                          placeholderTextColor={webColors.textMuted}
                          value={tempShiftDateTo}
                          onChangeText={(val) => {
                            setTempShiftDateTo(val);
                            setSelectedQuickRange(null);
                          }}
                          onFocus={(e) => { e.target.type = 'date'; }}
                          onBlur={(e) => { if (!tempShiftDateTo) e.target.type = 'text'; }}
                        />
                      </View>
                    </View>

                    {/* Quick Select */}
                    <Text style={sharedStyles.quickSelectLabel}>Quick Select</Text>
                    <View style={sharedStyles.quickSelectBtns}>
                      {QUICK_RANGE_OPTIONS.map((option) => (
                        <Pressable
                          key={option.value}
                          style={[sharedStyles.quickSelectBtn, selectedQuickRange === option.value && sharedStyles.quickSelectBtnActive]}
                          onPress={() => handleQuickRangeSelect(option.value)}
                        >
                          <Text style={[sharedStyles.quickSelectBtnText, selectedQuickRange === option.value && sharedStyles.quickSelectBtnTextActive]}>
                            {option.label}
                          </Text>
                        </Pressable>
                      ))}
                    </View>
                  </View>
                </View>
              )}

              {/* Footer */}
              <View style={sharedStyles.dateFilterFooter}>
                <Pressable
                  style={sharedStyles.dateFilterClearBtn}
                  onPress={() => {
                    setSelectedDatePosted(null);
                    setShiftDateFrom('');
                    setShiftDateTo('');
                    setTempShiftDateFrom('');
                    setTempShiftDateTo('');
                    setSelectedQuickRange(null);
                    setShowDatePostedDropdown(false);
                  }}
                >
                  <Text style={sharedStyles.dateFilterClearBtnText}>Clear</Text>
                </Pressable>
                <Pressable
                  style={sharedStyles.dateFilterApplyBtn}
                  onPress={() => {
                    if (currentDateTab === 'shift') {
                      setShiftDateFrom(tempShiftDateFrom);
                      setShiftDateTo(tempShiftDateTo);
                      if (tempShiftDateFrom || tempShiftDateTo) {
                        setSelectedDatePosted(null);
                      }
                    }
                    setShowDatePostedDropdown(false);
                  }}
                >
                  <Text style={sharedStyles.dateFilterApplyBtnText}>Apply</Text>
                </Pressable>
              </View>
            </View>
          </>
        )}
      </View>

      {/* Salary Range Dropdown */}
      <View style={sharedStyles.filterDropdownWrapper}>
        <Pressable
          style={[sharedStyles.filterPill, (salaryMin || salaryMax) && sharedStyles.filterPillActive, isMobile && sharedStyles.filterPillMobile]}
          onPress={() => {
            if (!showSalaryDropdown) {
              // Initialize temp values from actual values when opening
              setTempSalaryMin(salaryMin);
              setTempSalaryMax(salaryMax);
            }
            setShowSalaryDropdown(!showSalaryDropdown);
            setShowJobTypeDropdown(false);
            setShowCountryDropdown(false);
            setShowProfessionDropdown(false);
            setShowDatePostedDropdown(false);
          }}
        >
          <DollarSign size={16} color={(salaryMin || salaryMax) ? webColors.primary : webColors.textSecondary} />
          <Text style={[sharedStyles.filterPillText, (salaryMin || salaryMax) && sharedStyles.filterPillTextActive]}>
            {(salaryMin || salaryMax)
              ? `${getCurrencySymbol()} ${salaryMin || '0'} - ${salaryMax || '∞'}`
              : 'Salary'}
          </Text>
          <ChevronDown size={16} color={(salaryMin || salaryMax) ? webColors.primary : webColors.textSecondary} />
        </Pressable>
        {showSalaryDropdown && (
          <>
            {!isMobile && <Pressable style={sharedStyles.dropdownBackdrop} onPress={() => setShowSalaryDropdown(false)} />}
            <View style={[sharedStyles.filterDropdown, sharedStyles.salaryDropdown, isMobile && sharedStyles.filterDropdownMobile]}>
              <View style={sharedStyles.salaryDropdownHeader}>
                <Text style={sharedStyles.salaryDropdownTitle}>Salary Range</Text>
                {/* <Text style={sharedStyles.salaryDropdownSubtitle}>{getCurrencySymbol()} {getSalaryUnitLabel()}</Text> */}
              </View>
              <View style={sharedStyles.salaryInputsRow}>
                <View style={sharedStyles.salaryInputWrapper}>
                  <Text style={sharedStyles.salaryInputLabel}>Min</Text>
                  <View style={sharedStyles.salaryInputContainer}>
                    <Text style={sharedStyles.salaryInputPrefix}>{getCurrencySymbol()}</Text>
                    <TextInput
                      style={sharedStyles.salaryInput}
                      placeholder="0"
                      placeholderTextColor={webColors.textMuted}
                      value={tempSalaryMin}
                      onChangeText={(val) => setTempSalaryMin(val.replace(/[^0-9]/g, ''))}
                      keyboardType="numeric"
                    />
                  </View>
                </View>
                <Text style={sharedStyles.salaryInputDivider}>-</Text>
                <View style={sharedStyles.salaryInputWrapper}>
                  <Text style={sharedStyles.salaryInputLabel}>Max</Text>
                  <View style={sharedStyles.salaryInputContainer}>
                    <Text style={sharedStyles.salaryInputPrefix}>{getCurrencySymbol()}</Text>
                    <TextInput
                      style={sharedStyles.salaryInput}
                      placeholder="Any"
                      placeholderTextColor={webColors.textMuted}
                      value={tempSalaryMax}
                      onChangeText={(val) => setTempSalaryMax(val.replace(/[^0-9]/g, ''))}
                      keyboardType="numeric"
                    />
                  </View>
                </View>
              </View>
              <View style={sharedStyles.salaryDropdownFooter}>
                <Pressable
                  style={sharedStyles.salaryClearBtn}
                  onPress={() => {
                    setTempSalaryMin('');
                    setTempSalaryMax('');
                    setSalaryMin('');
                    setSalaryMax('');
                    setShowSalaryDropdown(false);
                  }}
                >
                  <Text style={sharedStyles.salaryClearBtnText}>Clear</Text>
                </Pressable>
                <Pressable
                  style={sharedStyles.salaryApplyBtn}
                  onPress={() => {
                    setSalaryMin(tempSalaryMin);
                    setSalaryMax(tempSalaryMax);
                    setShowSalaryDropdown(false);
                  }}
                >
                  <Text style={sharedStyles.salaryApplyBtnText}>Apply</Text>
                </Pressable>
              </View>
            </View>
          </>
        )}
      </View>

      {/* Clear Filters */}
      {(locationSearchQuery || selectedJobType !== null || selectedCountry !== 'MY' || selectedProfessions.length > 0 || selectedDatePosted !== null || shiftDateFrom || shiftDateTo || salaryMin || salaryMax) && (
        <Pressable
          style={sharedStyles.clearFiltersInline}
          onPress={() => {
            setLocationSearchQuery('');
            setSelectedJobType(null);
            setSelectedCountry(detectedCountry || 'MY');
            setGeolocationApplied(false);
            setSelectedProfessions([]);
            setSelectedDatePosted(null);
            setShiftDateFrom('');
            setShiftDateTo('');
            setTempShiftDateFrom('');
            setTempShiftDateTo('');
            setSelectedQuickRange(null);
            setSalaryMin('');
            setSalaryMax('');
          }}
        >
          <X size={14} color={webColors.textMuted} />
          <Text style={sharedStyles.clearFiltersInlineText}>Clear</Text>
        </Pressable>
      )}
    </>
  );

  return (
    <WebLayout navbarVariant="auth">
      {/* Header */}
      <View style={sharedStyles.pageHeaderContainer}>
        <View style={sharedStyles.pageHeader}>
          <Text style={[sharedStyles.pageTitle, isMobile && sharedStyles.pageTitleMobile]}>Explore Healthcare jobs, Find your Fit</Text>
          <Text style={[sharedStyles.pageSubtitle, isMobile && sharedStyles.pageSubtitleMobile]}>Welcome to the leading platform for medical professionals. Whether you're a doctor, nurse, pharmacist, dentist, physiotherapist, or allied health professional — explore full-time, part-time, and locum and International job opportunities that match your skills, schedule, and salary goals</Text>
        </View>

        {/* Search and Filters */}
        <View style={sharedStyles.searchSection}>
          <View style={[sharedStyles.searchRow, isMobile && sharedStyles.searchRowMobile]}>
            {/* Job Search Section */}
            <View style={[sharedStyles.searchSectionJob, isMobile && sharedStyles.searchSectionJobMobile]}>
              <View style={sharedStyles.searchIcon}>
                <Search size={20} color={webColors.textMuted} />
              </View>
              <TextInput
                style={sharedStyles.searchInput}
                placeholder="Job title, keywords, or company"
                placeholderTextColor={webColors.textMuted}
                value={searchQuery}
                onChangeText={setSearchQuery}
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity onPress={() => setSearchQuery('')} style={sharedStyles.clearBtn}>
                  <X size={14} color={webColors.textMuted} />
                </TouchableOpacity>
              )}
            </View>
            {/* Location Search Section */}
            <View style={[sharedStyles.searchSectionLocation, isMobile && sharedStyles.searchSectionLocationMobile]}>
              <View style={sharedStyles.searchIcon}>
                <MapPin size={20} color={webColors.textMuted} />
              </View>
              <TextInput
                style={sharedStyles.searchInput}
                placeholder="City, state or town"
                placeholderTextColor={webColors.textMuted}
                value={locationSearchQuery}
                onChangeText={setLocationSearchQuery}
              />
              {locationSearchQuery.length > 0 && (
                <TouchableOpacity onPress={() => setLocationSearchQuery('')} style={sharedStyles.clearBtn}>
                  <X size={14} color={webColors.textMuted} />
                </TouchableOpacity>
              )}
            </View>
          </View>

          {isMobile ? (
            <TouchableOpacity
              style={[sharedStyles.filtersButton, activeFilterCount > 0 && sharedStyles.filtersButtonActive]}
              onPress={() => setShowFiltersSheet(true)}
            >
              <Filter size={16} color={activeFilterCount > 0 ? webColors.primary : webColors.textSecondary} />
              <Text style={sharedStyles.filtersButtonText}>
                Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              </Text>
            </TouchableOpacity>
          ) : (
            <View style={sharedStyles.filterPillsRow}>
              {filterControls}
            </View>
          )}
        </View>
        {isMobile && showFiltersSheet && (
          <>
            <Pressable style={sharedStyles.filtersSheetOverlay} onPress={() => setShowFiltersSheet(false)} />
            <View style={sharedStyles.filtersSheet}>
              <View style={sharedStyles.filtersSheetHeader}>
                <Text style={sharedStyles.filtersSheetTitle}>Filters</Text>
                <TouchableOpacity onPress={() => setShowFiltersSheet(false)}>
                  <X size={22} color={webColors.textSecondary} />
                </TouchableOpacity>
              </View>
              <ScrollView style={sharedStyles.filtersSheetBody} contentContainerStyle={{ gap: 12 }}>
                <View style={[sharedStyles.filterPillsRow, sharedStyles.filterPillsRowMobile]}>
                  {filterControls}
                </View>
              </ScrollView>
              <View style={sharedStyles.filtersSheetFooter}>
                <TouchableOpacity
                  style={sharedStyles.filtersSheetApplyBtn}
                  onPress={() => setShowFiltersSheet(false)}
                >
                  <Text style={sharedStyles.filtersSheetApplyBtnText}>
                    Show {jobs.length} result{jobs.length !== 1 ? 's' : ''}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </>
        )}
      </View>

      {/* Results Count */}
      {!isLoading && jobs.length > 0 && (
        <View style={sharedStyles.resultsInfo}>
          <Text style={sharedStyles.resultsCount}>
            {jobs.length} job{jobs.length !== 1 ? 's' : ''} found
          </Text>
        </View>
      )}

      {/* Main Content - Master-Detail Layout */}
      <View style={[sharedStyles.masterDetailLayout, isMobile && sharedStyles.masterDetailLayoutMobile]}>
        {/* Left Panel - Job List */}
        {(!isMobile || mobileView === 'list') && (
        <View style={[sharedStyles.jobListPanel, isMobile && sharedStyles.jobListPanelMobile]}>
            {isLoading ? (
              <View style={sharedStyles.loadingContainer}>
                <ActivityIndicator size="large" color={webColors.primary} />
                <Text style={sharedStyles.loadingText}>Loading jobs...</Text>
              </View>
            ) : error ? (
              <View style={sharedStyles.errorState}>
                <View style={sharedStyles.errorIcon}>
                  <AlertTriangle size={48} color="#f59e0b" />
                </View>
                <Text style={sharedStyles.errorTitle}>Unable to load jobs</Text>
                <Text style={sharedStyles.errorText}>Please check your connection and try again</Text>
              </View>
            ) : paginatedJobs.length > 0 ? (
              <>
                <View style={sharedStyles.jobCardsList}>
                  {paginatedJobs.map((job) => (
                    <JobListCard
                      key={job.uuid || job.id}
                      job={job}
                      isSelected={selectedJob?.uuid === job.uuid}
                      onPress={() => {
                        setSelectedJob(job);
                        if (isMobile) setMobileView('detail');
                      }}
                    />
                  ))}
                </View>
                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={jobs.length}
                    pageSize={JOBS_PER_PAGE}
                    onPageChange={setCurrentPage}
                  />
                )}
              </>
            ) : (
              <View style={sharedStyles.emptyState}>
                <View style={sharedStyles.emptyIcon}>
                  <ClipboardList size={48} color="#9ca3af" />
                </View>
                <Text style={sharedStyles.emptyTitle}>No jobs found</Text>
                <Text style={sharedStyles.emptyText}>Try adjusting your search or filters</Text>
                {(searchQuery || locationSearchQuery || selectedJobType !== null || selectedCountry !== 'MY' || selectedProfessions.length > 0 || selectedDatePosted !== null || shiftDateFrom || shiftDateTo || salaryMin || salaryMax) && (
                  <TouchableOpacity
                    style={sharedStyles.clearFiltersBtn}
                    onPress={() => {
                      setSearchQuery('');
                      setLocationSearchQuery('');
                      setSelectedJobType(null);
                      setSelectedCountry(detectedCountry || 'MY');
                      setGeolocationApplied(false);
                      setSelectedProfessions([]);
                      setSelectedDatePosted(null);
                      setShiftDateFrom('');
                      setShiftDateTo('');
                      setTempShiftDateFrom('');
                      setTempShiftDateTo('');
                      setSelectedQuickRange(null);
                      setSalaryMin('');
                      setSalaryMax('');
                    }}
                  >
                    <Text style={sharedStyles.clearFiltersBtnText}>Clear Filters</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
        </View>
        )}

        {/* Right Panel - Job Detail */}
        {(!isMobile || mobileView === 'detail') && (
        <View style={[sharedStyles.jobDetailPanel, isMobile && sharedStyles.jobDetailPanelMobile]}>
          {isMobile && (
            <MobileDetailBackBar label="Back to results" onPress={() => setMobileView('list')} />
          )}
          <ScrollView style={[sharedStyles.detailScroll, isMobile && sharedStyles.detailScrollMobile]} showsVerticalScrollIndicator={true}>
            {!selectedJob ? (
              <View style={sharedStyles.noSelectionState}>
                <View style={sharedStyles.noSelectionIcon}>
                  <Briefcase size={48} color="#9ca3af" />
                </View>
                <Text style={sharedStyles.noSelectionText}>Select a job to view details</Text>
              </View>
            ) : detailLoading ? (
              <View style={sharedStyles.detailLoading}>
                <ActivityIndicator size="large" color={webColors.primary} />
                <Text style={sharedStyles.loadingText}>Loading details...</Text>
              </View>
            ) : (
              <JobDetailContent
                job={jobDetail}
                jobFacilityData={jobFacilityData}
                isFullTime={isFullTime}
                diffHours={diffHours}
                displayCurrency={displayCurrency}
                isFavorited={false}
                onApply={handleApply}
                onToggleFavorite={handleApply}
                shareUrl={isDoctor ? `https://hiresupermedic.com/job/${selectedJob?.uuid}` : `https://employer.hiresupermedic.com/job/${selectedJob?.uuid}`}
              />
            )}
          </ScrollView>
        </View>
        )}
      </View>

      {/* Auth Choice Modal */}
      <AuthChoiceModal
        visible={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        jobTitle={jobDetail?.title}
      />
    </WebLayout>
  );
};

export default PublicJobsScreen;
